import { useState } from "react";
import {
  Building2, ChevronDown, ChevronRight, Check, Network, Layers,
} from "lucide-react";
import { SelectedLabel } from "../../components/Dropdown";

/* A selectable config scope — a GROUP (cascades to its orgs) or an ORGANIZATION. */
export interface ScopeOption {
  id: string;
  name: string;
  type?: string;      // "GROUP" | "ORGANIZATION"
  typeLabel?: string; // "Group" | "Organization"
}
/* A group with the organizations that live beneath it. */
export interface ScopeGroup {
  id: string;
  name: string;
  orgs: ScopeOption[];
}

const UNGROUPED = "__ungrouped__";

/* Two-panel scope picker: groups on the left (each directly selectable via its
   checkbox — picking a group cascades to all its orgs, which then show as
   auto-checked on the right). You can also drill in and pick a single org. */
export function ScopePicker({
  groups, ungrouped, selectedId, selected, onSelect,
}: {
  groups: ScopeGroup[];
  ungrouped: ScopeOption[];
  selectedId: string | null;
  selected: ScopeOption | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  /* which group's orgs are shown on the right */
  const [activeId, setActiveId] = useState<string | null>(null);

  const groupOf = (id: string | null) =>
    groups.find((g) => g.id === id || g.orgs.some((o) => o.id === id))?.id ??
    (ungrouped.some((o) => o.id === id) ? UNGROUPED : null);

  const toggle = () => {
    if (!open) {
      setActiveId(groupOf(selectedId) ?? groups[0]?.id ?? (ungrouped.length ? UNGROUPED : null));
    }
    setOpen((o) => !o);
  };

  const activeGroup = groups.find((g) => g.id === activeId);
  const activeOrgs = activeId === UNGROUPED ? ungrouped : activeGroup?.orgs ?? [];
  const activeName = activeId === UNGROUPED ? "Ungrouped organizations" : activeGroup?.name ?? "";
  /* group is the chosen scope → every org beneath it is covered (auto-checked) */
  const groupCovered = !!activeGroup && selectedId === activeGroup.id;

  /* picking a group selects it (cascade) and reveals its orgs; keep open so the
     auto-check is visible and a single org can still be drilled into */
  const pickGroup = (id: string) => { onSelect(id); setActiveId(id); };
  const pickOrg = (id: string) => { onSelect(id); setOpen(false); };
  const empty = groups.length === 0 && ungrouped.length === 0;

  return (
    <div className="relative self-start sm:self-auto">
      {/* Trigger */}
      <button
        type="button"
        onClick={toggle}
        className="flex h-10 items-center gap-2 rounded-lg border border-border bg-card px-3.5 text-sm font-medium text-foreground/80 transition-colors hover:border-indigo-400/40 hover:text-foreground"
      >
        {selected?.type === "GROUP"
          ? <Network className="size-4 shrink-0 text-indigo-400" />
          : <Building2 className="size-4 shrink-0 text-indigo-400" />}
        <SelectedLabel
          label={selected?.name ?? "Select scope"}
          className="min-w-0 max-w-[180px] truncate"
        />
        {selected?.typeLabel && (
          <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide ${
              selected.type === "GROUP"
                ? "bg-indigo-500/15 text-indigo-500"
                : "bg-foreground/[0.06] text-muted-foreground"
            }`}
          >
            {selected.typeLabel}
          </span>
        )}
        <ChevronDown
          className={`size-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1.5 flex w-[480px] max-w-[92vw] overflow-hidden rounded-xl border border-border bg-popover shadow-xl">
            {empty ? (
              <p className="w-full px-4 py-6 text-center text-xs text-muted-foreground">No scopes available</p>
            ) : (
              <>
                {/* LEFT — groups (each directly selectable) */}
                <div className="flex w-[200px] shrink-0 flex-col border-r border-border">
                  <div className="border-b border-foreground/[0.05] px-3 py-2">
                    <span className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                      Groups
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto py-1">
                    {groups.map((g) => {
                      const isActive = g.id === activeId;
                      const isSelected = g.id === selectedId;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => pickGroup(g.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs transition-colors ${
                            isActive ? "bg-indigo-500/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.04]"
                          }`}
                        >
                          <span
                            className={`flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                              isSelected ? "border-indigo-500 bg-indigo-500 text-white" : "border-border"
                            }`}
                          >
                            {isSelected && <Check className="size-3" />}
                          </span>
                          <Network
                            className={`size-3.5 shrink-0 ${isActive || isSelected ? "text-indigo-500" : "text-muted-foreground"}`}
                          />
                          <SelectedLabel label={g.name} className="min-w-0 flex-1 truncate" />
                          <ChevronRight
                            className={`size-3.5 shrink-0 ${isActive ? "text-indigo-500" : "text-muted-foreground/40"}`}
                          />
                        </button>
                      );
                    })}
                    {ungrouped.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setActiveId(UNGROUPED)}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs transition-colors ${
                          activeId === UNGROUPED ? "bg-indigo-500/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.04]"
                        }`}
                      >
                        <span className="size-4 shrink-0" />
                        <Layers className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="flex-1 truncate">Ungrouped</span>
                        <ChevronRight
                          className={`size-3.5 shrink-0 ${activeId === UNGROUPED ? "text-indigo-500" : "text-muted-foreground/40"}`}
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* RIGHT — organizations of the active group */}
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="border-b border-foreground/[0.05] px-3 py-2">
                    <SelectedLabel
                      label={activeName}
                      className="block min-w-0 truncate text-[11px] font-semibold text-foreground"
                    />
                  </div>
                  <div className="max-h-72 overflow-y-auto py-1">
                    {activeOrgs.length === 0 ? (
                      <p className="px-3 py-3 text-[11px] text-muted-foreground">No organizations here yet.</p>
                    ) : (
                      activeOrgs.map((o) => {
                        const orgSelected = o.id === selectedId;
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => pickOrg(o.id)}
                            className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-xs transition-colors hover:bg-foreground/[0.04] ${
                              orgSelected ? "text-foreground" : "text-muted-foreground"
                            }`}
                          >
                            <span
                              className={`flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                orgSelected
                                  ? "border-indigo-500 bg-indigo-500 text-white"
                                  : groupCovered
                                    ? "border-indigo-400/50 bg-indigo-500/25 text-white/90"
                                    : "border-border"
                              }`}
                            >
                              {(orgSelected || groupCovered) && <Check className="size-3" />}
                            </span>
                            <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
                            <SelectedLabel label={o.name} className="min-w-0 flex-1 truncate" />
                            {groupCovered && !orgSelected && (
                              <span className="shrink-0 text-[9px] font-medium tracking-wide text-indigo-400/70">
                                via group
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
